import {
  BaseQueryApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  QueryReturnValue,
} from "@reduxjs/toolkit/query/react"
import { ADMIN_LEGACY_API_URL, ADMIN_V1_API_URL } from "../../config/env"
import { logout } from "../../store/authSlice"
import type { ApiEnvelope, ApiError } from "../../types/api"

type AdminFetchArgs = FetchArgs & { adminApi?: "legacy" | "v1" }

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ADMIN_LEGACY_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as any).auth?.token
    if (token) headers.set("Authorization", `Bearer ${token}`)
    return headers
  },
})

function normalizeError(error: FetchBaseQueryError): ApiError {
  const status = typeof error.status === "number" ? error.status : 500
  const payload = (error as any).data
  if (payload?.error?.message) {
    return {
      status,
      message: payload.error.message,
      code: payload.error.code,
      details: payload.error.details,
      requestId: payload.error.requestId,
    }
  }
  if (payload?.error) {
    return { status, message: String(payload.error), details: payload }
  }
  if (payload?.message) {
    return { status, message: String(payload.message), details: payload }
  }
  return { status, message: "Unexpected API error", details: payload }
}

function unwrapEnvelope<T>(data: unknown): T {
  const envelope = data as ApiEnvelope<T>
  if (envelope && typeof envelope === "object" && "success" in envelope && envelope.success) {
    return envelope.data
  }
  return data as T
}

function normalizeArgs(args: string | AdminFetchArgs): string | FetchArgs {
  if (typeof args === "string") {
    if (args.startsWith("/v1/")) return `${ADMIN_V1_API_URL}${args.slice(3)}`
    return args
  }

  const { adminApi, ...fetchArgs } = args
  if (adminApi === "v1" || fetchArgs.url.startsWith("/v1/")) {
    return {
      ...fetchArgs,
      url: `${ADMIN_V1_API_URL}${fetchArgs.url.replace(/^\/v1/, "")}`,
    }
  }
  return fetchArgs
}

export async function baseQueryWithAuthAndEnvelope<T = unknown>(
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>
): Promise<QueryReturnValue<T, ApiError>> {
  const result = await rawBaseQuery(normalizeArgs(args as string | AdminFetchArgs), api, extraOptions)
  if (result.error) {
    const normalized = normalizeError(result.error)
    if (normalized.status === 401) {
      api.dispatch(logout())
    }
    return { error: normalized }
  }
  return { data: unwrapEnvelope<T>(result.data) }
}
